import dayjs from "dayjs";
import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

/** Material-UI */
import {
    Box,
    Button,
    Collapse,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    useMediaQuery,
} from "@material-ui/core";
import { CalendarTodayOutlined, Done, ExpandLess, ExpandMore, SettingsBackupRestore } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

// https://web.archive.org/web/20230107144512/https://material-ui-pickers.dev/getting-started/installation
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";

import DateFnsUtils from "@date-io/date-fns";
import enLocale from "date-fns/locale/en-US";
import plLocale from "date-fns/locale/pl";

const localeMap = {
    en: enLocale,
    pl: plLocale,
};

const useStyles = makeStyles((theme) => ({
    root: {},
    paper: {
        marginTop: theme.spacing(6),
    },
    dividerTop: {
        margin: theme.spacing(10, 0, 6, 0),
    },
    dividerBottom: {
        margin: theme.spacing(6, 0, 10, 0),
        [theme.breakpoints.down('sm')]:{
            margin: theme.spacing(4, 0, 4, 0),
        }
    },
    showButton: {
        textTransform: "capitalize",
        fontSize: "clamp(1rem, 2vw, 1.5rem)",
        letterSpacing: 0,
    },
    textField: {
        [theme.breakpoints.down("sm")]: {
            marginBottom: 16,
        },
        "& label": {
            fontSize: "clamp(13px, 1vw, 1.125rem)",
        },
        "& fieldset": {
            [theme.breakpoints.down("sm")]: {
                top: -7,
            },
        },
        "& legend": {
            fontSize: "0.835em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.6em",
            },
        },
    },
    input: {
        fontSize: "clamp(13px, 1vw, 1.125rem)",
    },
    label: {
        fontSize: "clamp(13px, 1vw, 1.125rem)",
    },
    inputAdornmentEnd: {},
    datePickerButtonRoot: {
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
    adornedEnd: {
        paddingRight: 0,
    },
    helperText: {
        fontSize: "clamp(12px, 1vw, 1rem)",
        [theme.breakpoints.down("sm")]: {
            marginTop: 0,
        },
    },
    button: {
        minWidth: 250,
        fontSize: "clamp(12px, 1vw, 14px)",
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 0, 0),
            width: "100%",
            height: 36,
            paddingTop: 4,
        },
    },
}));

const DropDownIcon = forwardRef((props, ref) => (
    <IconButton ref={ref} {...props} size={"small"}>
        <ExpandMore color={"primary"} fontSize={"small"} />
    </IconButton>
));

const DEFAULTS = {
    search: "",
    limit: 10,
    page: 1,
    sys: "",
    from: "",
    to: "",
    api: "A",
    form: "A",
    state: "A",
    obj: "",
};

const FilterSystems = ({ filterOn, onFilter = () => {} }) => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();

    const history = useHistory();

    const isMobile = useMediaQuery("(max-width: 599.95px)");

    /** state */
    const [SHOW, setSHOW] = useState(true);

    const [API, setAPI] = useState("A");
    const [FORM, setFORM] = useState("A");
    const [FROM, setFROM] = useState(null);
    const [OBJECT_NAME, setOBJECT_NAME] = useState("");
    const [STATE, setSTATE] = useState("A");
    const [SYSTEM_NAME, setSYSTEM_NAME] = useState("");
    const [TO, setTO] = useState(null);

    const [CHANGED, setCHANGED] = useState({ ...DEFAULTS, equal: true, submitted: true });

    /** effect */
    useEffect(() => {
        const { equal, submitted, ..._prev } = CHANGED;

        const _changed = {
            search: "",
            limit: 10,
            page: 1,
            sys: SYSTEM_NAME,
            from: FROM ? dayjs(FROM).format("YYYYMMDD") : "",
            to: TO ? dayjs(TO).format("YYYYMMDD") : "",
            api: API,
            form: FORM,
            state: STATE,
            obj: OBJECT_NAME,
        };

        const _equal = JSON.stringify(_changed) === JSON.stringify(DEFAULTS);

        const _submitted = JSON.stringify(_changed) === JSON.stringify(_prev);
        if (_submitted) return;

        const _res = { ..._changed, equal: _equal, submitted: _submitted };

        if (JSON.stringify(CHANGED) !== JSON.stringify(_res)) {
            setCHANGED(_res);
        }
    }, [API, FORM, FROM, OBJECT_NAME, STATE, SYSTEM_NAME, TO, CHANGED]);

    /** handlers */
    const handleShow = () => setSHOW(!SHOW);

    const handleSystemName = (event) => setSYSTEM_NAME(event.target.value);
    const handleObjectName = (event) => setOBJECT_NAME(event.target.value);
    const handleFrom = (date) => setFROM(date);
    const handleTo = (date) => setTO(date);
    const handleAPI = (event) => setAPI(event.target.value);
    const handleForm = (event) => setFORM(event.target.value);
    const handleState = (event) => setSTATE(event.target.value);

    const handleSubmit = () => {
        onFilter && onFilter({ loading: true });

        const { equal, submitted, ...data } = CHANGED;

        const newQuery = new URLSearchParams(data).toString();
        // const newPath = `${history.location.pathname}?${newQuery}${history.location.hash}`;
        const newPath = `${newQuery}${history.location.hash}`;

        // history.push(newPath, { shallow: true });

        onFilter && onFilter({ query: newPath });

        setCHANGED({ ...CHANGED, submitted: true });
    };

    const handleReset = () => {
        setAPI(DEFAULTS.api);
        setFORM(DEFAULTS.form);
        setFROM(DEFAULTS.from);
        setOBJECT_NAME(DEFAULTS.obj);
        setSTATE(DEFAULTS.state);
        setSYSTEM_NAME(DEFAULTS.sys);
        setTO(DEFAULTS.to);
        setCHANGED({ ...CHANGED, submitted: true });
    };

    /** selects */
    const optionsAPI = [
        { label: t("Wszystkie (domyślne)"), value: "A", default: true },
        { label: t("Tak"), value: "Y" },
        { label: t("Nie"), value: "N" },
    ];

    const optionsForm = [
        { label: t("Wszystkie (domyślne)"), value: "A", default: true },
        { label: t("Papierowa"), value: "P" },
        { label: t("Elektroniczna"), value: "E" },
    ];

    const optionsState = [
        { label: t("Wszystkie (domyślne)"), value: "A", default: true },
        { label: t("Wdrożony"), value: "W" },
        { label: t("Niewdrożony"), value: "N" },
        { label: t("Częściowo wdrożony"), value: "C" },
    ];

    if (!filterOn) {
        return null;
    }

    return (
        <>
            <Divider className={classes.dividerTop} />

            <Grid container className={classes.root} spacing={isMobile ? 0 : 6}>
                <Grid item xs={12}>
                    <Button
                        color={"primary"}
                        onClick={handleShow}
                        fullWidth
                        className={classes.showButton}
                        endIcon={
                            SHOW ? (
                                <ExpandLess fontSize={"small"} color={"primary"} />
                            ) : (
                                <ExpandMore fontSize={"small"} color={"primary"} />
                            )
                        }
                    >
                        {t("Filtrowanie")}
                    </Button>
                </Grid>
            </Grid>

            <Collapse in={SHOW}>
                <Grid container className={classes.paper} spacing={isMobile ? 0 : 6}>
                    <Grid container item xs={12} spacing={isMobile ? 0 : 6}>
                        <Grid container item xs={12} lg={7} spacing={isMobile ? 0 : 6}>
                            <Grid item xs={12}>
                                <TextField
                                    id="filter-system-name"
                                    label={t("Nazwa systemu informacyjnego")}
                                    value={SYSTEM_NAME}
                                    onChange={handleSystemName}
                                    variant="outlined"
                                    fullWidth
                                    helperText={t("Wpisz minimum 1 znak")}
                                    size={isMobile ? "small" : "medium"}
                                    FormHelperTextProps={{ className: classes.helperText }}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md>
                                <TextField
                                    id="filter-api"
                                    select
                                    label={t("Czy jest API?")}
                                    value={API}
                                    onChange={handleAPI}
                                    variant="outlined"
                                    fullWidth
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            // disablePortal: true,
                                            // disableScrollLock: true,
                                        },
                                    }}
                                    helperText={" "}
                                    size={isMobile ? "small" : "medium"}
                                    FormHelperTextProps={{ className: classes.helperText }}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                >
                                    {optionsAPI.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Box component={option.default ? "em" : "span"} className={classes.label}>
                                                {option.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md>
                                <TextField
                                    id="filter-form"
                                    select
                                    label={t("Forma prowadzenia systemu")}
                                    value={FORM}
                                    onChange={handleForm}
                                    variant="outlined"
                                    fullWidth
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            // disablePortal: true,
                                            // disableScrollLock: true,
                                        },
                                    }}
                                    helperText={" "}
                                    size={isMobile ? "small" : "medium"}
                                    FormHelperTextProps={{ className: classes.helperText }}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                >
                                    {optionsForm.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Box component={option.default ? "em" : "span"} className={classes.label}>
                                                {option.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md>
                                <TextField
                                    id="filter-state"
                                    select
                                    label={t("Stan wdrożenia")}
                                    value={STATE}
                                    onChange={handleState}
                                    variant="outlined"
                                    fullWidth
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            // disablePortal: true,
                                            // disableScrollLock: true,
                                        },
                                    }}
                                    helperText={" "}
                                    size={isMobile ? "small" : "medium"}
                                    FormHelperTextProps={{ className: classes.helperText }}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                >
                                    {optionsState.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Box component={option.default ? "em" : "span"} className={classes.label}>
                                                {option.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Grid container item xs={12} lg spacing={isMobile ? 0 : 6}>
                            <Grid item xs={12} md>
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[i18n.language]}>
                                    <KeyboardDatePicker
                                        autoOk
                                        variant="inline"
                                        inputVariant="outlined"
                                        label={t("Termin wdrożenia (od)")}
                                        format="yyyyMMdd"
                                        value={FROM || null}
                                        InputProps={{
                                            classes: { adornedEnd: classes.adornedEnd },
                                        }}
                                        KeyboardButtonProps={{ classes: { root: classes.datePickerButtonRoot } }}
                                        InputAdornmentProps={{
                                            position: "end",
                                            classes: { positionEnd: classes.inputAdornmentEnd },
                                        }}
                                        keyboardIcon={<CalendarTodayOutlined fontSize={"small"} />}
                                        onChange={handleFrom}
                                        helperText={t("Format daty{{colon}} RRRRMMDD", { colon: ":" })}
                                        fullWidth
                                        size={isMobile ? "small" : "medium"}
                                        FormHelperTextProps={{ className: classes.helperText }}
                                        classes={{ root: classes.textField }}
                                        inputProps={{
                                            className: classes.input,
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid item xs={12} md>
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[i18n.language]}>
                                    <KeyboardDatePicker
                                        autoOk
                                        variant="inline"
                                        inputVariant="outlined"
                                        label={t("Termin wdrożenia (do)")}
                                        format="yyyyMMdd"
                                        value={TO || null}
                                        InputProps={{
                                            classes: { adornedEnd: classes.adornedEnd },
                                        }}
                                        KeyboardButtonProps={{ classes: { root: classes.datePickerButtonRoot } }}
                                        InputAdornmentProps={{
                                            position: "end",
                                            classes: { positionEnd: classes.inputAdornmentEnd },
                                        }}
                                        keyboardIcon={<CalendarTodayOutlined fontSize={"small"} />}
                                        onChange={handleTo}
                                        helperText={t("Format daty{{colon}} RRRRMMDD", { colon: ":" })}
                                        fullWidth
                                        size={isMobile ? "small" : "medium"}
                                        FormHelperTextProps={{ className: classes.helperText }}
                                        classes={{ root: classes.textField }}
                                        inputProps={{
                                            className: classes.input,
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    id="filter-object-name"
                                    label={t("Nazwa obiektu")}
                                    value={OBJECT_NAME}
                                    onChange={handleObjectName}
                                    variant="outlined"
                                    fullWidth
                                    helperText={t("Wpisz minimum 1 znak")}
                                    size={isMobile ? "small" : "medium"}
                                    FormHelperTextProps={{ className: classes.helperText }}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item xs={12} spacing={isMobile ? 0 : 6}>
                            <Grid
                                item
                                xs={12}
                                md
                                style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
                            >
                                <Button
                                    color={"primary"}
                                    variant={"outlined"}
                                    startIcon={<SettingsBackupRestore />}
                                    className={classes.button}
                                    onClick={handleReset}
                                    disabled={!!CHANGED.equal}
                                    size={isMobile ? "small" : "medium"}
                                    fullWidth={!!isMobile}
                                >
                                    {t("PRZYWRÓĆ DOMYŚLNE")}
                                </Button>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md
                                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
                            >
                                <Button
                                    color={"primary"}
                                    variant={"contained"}
                                    startIcon={<Done />}
                                    className={classes.button}
                                    style={{ marginTop: isMobile ? 16 : undefined }}
                                    onClick={handleSubmit}
                                    disabled={!!CHANGED.submitted}
                                    size={isMobile ? "small" : "medium"}
                                    fullWidth={!!isMobile}
                                >
                                    {t("ZASTOSUJ ZMIANY")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Collapse>

            <Divider className={classes.dividerBottom} />
        </>
    );
};

export default FilterSystems;
